import React, { useRef, useState, useEffect, useCallback } from "react";
// import './VideoPlayer.css'

import "../styles/VideoPlayer.css";

const VideoPlayer = ({ videoUrl }) => {
  const videoRef = useRef(null);
  const videoContainerRef = useRef(null);
  const timelineContainerRef = useRef(null);
  const previewImgRef = useRef(null);
  const thumbnailImgRef = useRef(null);

  const [isScrubbing, setIsScrubbing] = useState(false);
  const [wasPaused, setWasPaused] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [totalTime, setTotalTime] = useState(0);
  const [volumeLevel, setVolumeLevel] = useState("high");
  const [playbackRate, setPlaybackRate] = useState(1);
  //   const [captionsShown, setCaptionsShown] = useState(false);

  const formatDuration = (time) => {
    const seconds = Math.floor(time % 60);
    const minutes = Math.floor(time / 60) % 60;
    const hours = Math.floor(time / 3600);
    const leadingZero = (num) => num.toString().padStart(2, "0");

    if (hours === 0) {
      return `${minutes}:${leadingZero(seconds)}`;
    } else {
      return `${hours}:${leadingZero(minutes)}:${leadingZero(seconds)}`;
    }
  };

  //------ Toggle Play/Pause ------//
  const togglePlay = async () => {
    const video = videoRef.current;
    if (!video) return;
  
    if (video.paused) {
      try {
        await video.play();
      } catch (error) {
        console.error("Error attempting to play:", error);
      }
    } else {
      video.pause();
    }
  };
  //----- Toggle Full-Screen Mode ------//
  const toggleFullScreenMode = () => {
    const videoContainer = videoContainerRef.current;
    if (!videoContainer) return;

    if (!document.fullscreenElement) {
      videoContainer.requestFullscreen();
    } else {
      document.exitFullscreen();
    }
  };

  //------ Toggle Theater Mode ------//
  const toggleTheaterMode = () => {
    videoContainerRef.current.classList.toggle("theater");
  };

  //------ Toggle Mini Player Mode ------//
  const toggleMiniPlayerMode = () => {
    const video = videoRef.current;
    if (!video) return;

    if (document.pictureInPictureElement) {
      document.exitPictureInPicture();
    } else {
      video.requestPictureInPicture().catch((error) => {
        console.error("Picture-in-Picture failed:", error);
      });
    }
  };

  //------ Toggle Mute ------//
  const toggleMute = () => {
    const video = videoRef.current;
    if (!video) return;

    video.muted = !video.muted;
  };

  //------ Toggle Captions ------//
  const toggleCaptions = () => {
    const video = videoRef.current;
    if (!video) return;

    const captions = video.textTracks[0];
    if (captions.mode === "hidden") {
      captions.mode = "showing";
      //   setCaptionsShown(true);
      videoContainerRef.current.classList.add("captions");
    } else {
      captions.mode = "hidden";
      //   setCaptionsShown(false);
      videoContainerRef.current.classList.remove("captions");
    }
  };

  //------ Change Playback Speed ------//
  const changePlaybackSpeed = () => {
    const video = videoRef.current;
    if (!video) return;

    let newPlaybackRate = video.playbackRate + 0.25;
    if (newPlaybackRate > 2) newPlaybackRate = 0.25;
    video.playbackRate = newPlaybackRate;
    setPlaybackRate(newPlaybackRate);
  };

  //------ Skip Forward or Backward ------//
  const skip = (duration) => {
    const video = videoRef.current;
    if (!video) return;

    video.currentTime += duration;
  };

  //------ Handle Timeline Update ------//
  const handleTimelineUpdate = useCallback(
    (e) => {
      const video = videoRef.current;
      const timeline = timelineContainerRef.current;
      if (!video || !timeline) return;

      const rect = timeline.getBoundingClientRect();
      const percent =
        Math.min(Math.max(0, e.clientX - rect.left), rect.width) / rect.width;
      const previewImgNumber = Math.max(
        1,
        Math.floor((percent * video.duration) / 10)
      );
      const previewImgSrc = `assets/previewImgs/preview${previewImgNumber}.jpg`;

      if (previewImgRef.current) {
        previewImgRef.current.src = previewImgSrc;
      }

      timeline.style.setProperty("--preview-position", percent);

      if (isScrubbing) {
        e.preventDefault();
        if (thumbnailImgRef.current) {
          thumbnailImgRef.current.src = previewImgSrc;
        }
        timeline.style.setProperty("--progress-position", percent);
      }
    },
    [isScrubbing]
  );

  //------ Toggle Scrubbing ------//
  const toggleScrubbing = useCallback(
    (e) => {
      const video = videoRef.current;
      const timeline = timelineContainerRef.current;
      const videoContainer = videoContainerRef.current;
      if (!video || !timeline || !videoContainer) return;

      const rect = timeline.getBoundingClientRect();
      const percent =
        Math.min(Math.max(0, e.clientX - rect.left), rect.width) / rect.width;
      const scrubbing = (e.buttons & 1) === 1;

      setIsScrubbing(scrubbing);
      videoContainer.classList.toggle("scrubbing", scrubbing);

      if (scrubbing) {
        setWasPaused(video.paused);
        video.pause();
      } else {
        video.currentTime = percent * video.duration;
        if (!wasPaused) video.play();
      }

      handleTimelineUpdate(e);
    },
    [handleTimelineUpdate, wasPaused]
  );

//------ Handle Volume Slider Change ------//
const handleVolumeChange = (e) => {
  const video = videoRef.current;
  if (!video) return;

  const value = parseFloat(e.target.value);
  video.volume = value;
  video.muted = value === 0;

  // Update the CSS custom property for the slider fill
  e.target.style.setProperty("--fill-percent", `${value
 * 100}%`);
};


  //------ Keyboard Shortcuts ------//
  useEffect(() => {
    const handleKeyDown = (e) => {
      const tagName = document.activeElement.tagName.toLowerCase();

      if (tagName === "input") return;

      switch (e.key.toLowerCase()) {
        case " ":
          if (tagName === "button") return;
          e.preventDefault();
          break;
        case "k":
          togglePlay();
          break;
        case "f":
          toggleFullScreenMode();
          break;
        case "t":
          toggleTheaterMode();
          break;
        case "i":
          toggleMiniPlayerMode();
          break;
        case "m":
          toggleMute();
          break;
        case "arrowleft":
        case "j":
          skip(-5);
          break;
        case "arrowright":
        case "l":
          skip(5);
          break;
        case "c":
          toggleCaptions();
          break;
        default:
          break;
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isScrubbing, wasPaused]);

  //------ Video Event Listeners ------//
  useEffect(() => {
    const video = videoRef.current;

    if (!video) return;

    const handleLoadedData = () => {
      setTotalTime(video.duration);
    };

    const handleTimeUpdate = () => {
      setCurrentTime(video.currentTime);
      const percent = video.currentTime / video.duration;
      timelineContainerRef.current.style.setProperty(
        "--progress-position",
        percent
      );
    };

    const handleVolumeChange = () => {
      const volume = video.volume;
      let level = "low";
      if (video.muted || volume === 0) {
        level = "muted";
      } else if (volume >= 0.5) {
        level = "high";
      }
      setVolumeLevel(level);
    };

    const handlePlay = () => {
      videoContainerRef.current.classList.remove("paused");
    };

    const handlePause = () => {
      videoContainerRef.current.classList.add("paused");
    };

    video.addEventListener("loadeddata", handleLoadedData);
    video.addEventListener("timeupdate", handleTimeUpdate);
    video.addEventListener("volumechange", handleVolumeChange);
    video.addEventListener("play", handlePlay);
    video.addEventListener("pause", handlePause);

    return () => {
      video.removeEventListener("loadeddata", handleLoadedData);
      video.removeEventListener("timeupdate", handleTimeUpdate);
      video.removeEventListener("volumechange", handleVolumeChange);
      video.removeEventListener("play", handlePlay);
      video.removeEventListener("pause", handlePause);
    };
  }, []);

  //------ Timeline Event Listeners ------//
  useEffect(() => {
    const timeline = timelineContainerRef.current;

    if (!timeline) return;

    const handleMouseMove = (e) => handleTimelineUpdate(e);
    const handleMouseDown = (e) => toggleScrubbing(e);
    const handleMouseUp = (e) => {
      if (isScrubbing) toggleScrubbing(e);
    };
    const handleMouseMoveDocument = (e) => {
      if (isScrubbing) handleTimelineUpdate(e);
    };

    timeline.addEventListener("mousemove", handleMouseMove);
    timeline.addEventListener("mousedown", handleMouseDown);
    document.addEventListener("mouseup", handleMouseUp);
    document.addEventListener("mousemove", handleMouseMoveDocument);

    return () => {
      timeline.removeEventListener("mousemove", handleMouseMove);
      timeline.removeEventListener("mousedown", handleMouseDown);
      document.removeEventListener("mouseup", handleMouseUp);
      document.removeEventListener("mousemove", handleMouseMoveDocument);
    };
  }, [isScrubbing, wasPaused, handleTimelineUpdate, toggleScrubbing]);

  //------ Fullscreen Change Listener ------//
  useEffect(() => {
    const handleFullscreenChange = () => {
      videoContainerRef.current.classList.toggle(
        "full-screen",
        !!document.fullscreenElement
      );
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);

    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
    };
  }, []);

  //------ Picture-in-Picture Event Listeners ------//
  useEffect(() => {
    const video = videoRef.current;
    const videoContainer = videoContainerRef.current;

    if (!video || !videoContainer) return;

    const handleEnterPip = () => {
      videoContainer.classList.add("mini-player");
    };

    const handleLeavePip = () => {
      videoContainer.classList.remove("mini-player");
    };

    video.addEventListener("enterpictureinpicture", handleEnterPip);
    video.addEventListener("leavepictureinpicture", handleLeavePip);

    return () => {
      video.removeEventListener("enterpictureinpicture", handleEnterPip);
      video.removeEventListener("leavepictureinpicture", handleLeavePip);
    };
  }, []);

    //------ Reset Video Player on videoUrl Change ------//
    useEffect(() => {
      const resetVideoPlayer = () => {
        //----- Reset state variables -----//
        setIsScrubbing(false);
        setWasPaused(false);
        setCurrentTime(0);
        setTotalTime(0);
        setVolumeLevel("high");
        setPlaybackRate(1);
  
        //----- Reset video element -----//
        const video = videoRef.current;
        if (video) {
          video.pause();
          video.currentTime = 0;
          video.volume = 1;
          video.playbackRate = 1;
          video.muted = false;
          video.removeAttribute("src");
          setTimeout(() => {
            video.src = videoUrl;
            video.load();
          }, 0);
        }
  
        //----- Reset CSS classes -----//
        const videoContainer = videoContainerRef.current;
        if (videoContainer) {
          videoContainer.classList.remove(
            "paused",
            "theater",
            "full-screen",
            "mini-player",
            "captions",
            "scrubbing"
          );
          videoContainer.classList.add("paused");
        }
        if (thumbnailImgRef.current) {
          thumbnailImgRef.current.src = "";
        }
        setPlaybackRate(1);
      };
  
      resetVideoPlayer();
    }, [videoUrl]);

  return (
    <div
      className={`videoplayer-video-container paused`}
      data-volume-level={volumeLevel}
      ref={videoContainerRef}
    >
      <img
        className="videoplayer-thumbnail-img"
        ref={thumbnailImgRef}
        alt="Thumbnail Preview"
      />
      <div className="videoplayer-video-controls-container">
        <div
          className="videoplayer-timeline-container"
          ref={timelineContainerRef}
        >
          <div className="videoplayer-timeline">
            <img
              className="videoplayer-preview-img"
              ref={previewImgRef}
              alt="Timeline Preview"
            />
            <div className="videoplayer-thumb-indicator"></div>
          </div>
        </div>
        <div className="videoplayer-controls">
          {/*------ Play/Pause Button ------*/}
          <button
            className="videoplayer-play-pause-btn"
            onClick={togglePlay}
            title="Play/Pause"
            aria-label="Play/Pause"
          >
            <svg className="videoplayer-play-icon" viewBox="0 0 24 24">
              <path fill="currentColor" d="M8,5.14V19.14L19,12.14L8,5.14Z" />
            </svg>
            <svg className="videoplayer-pause-icon" viewBox="0 0 24 24">
              <path fill="currentColor" d="M14,19H18V5H14M6,19H10V5H6V19Z" />
            </svg>
          </button>

          {/*------ Volume Controls ------*/}
          <div className="videoplayer-volume-container">
            <button
              className="videoplayer-mute-btn"
              onClick={toggleMute}
              title="Mute/Unmute"
              aria-label="Mute/Unmute"
            >
              <svg className="videoplayer-volume-high-icon" viewBox="0 0 24 24">
                <path
                  fill="currentColor"
                  d="M14,3.23V5.29C16.89,6.15 19,8.83 19,12C19,15.17 16.89,17.84 14,18.7V20.77C18,19.86 21,16.28 21,12C21,7.72 18,4.14 14,3.23M16.5,12C16.5,10.23 15.5,8.71 14,7.97V16C15.5,15.29 16.5,13.76 16.5,12M3,9V15H7L12,20V4L7,9H3Z"
                />
              </svg>
              <svg className="videoplayer-volume-low-icon" viewBox="0 0 24 24">
                <path
                  fill="currentColor"
                  d="M5,9V15H9L14,20V4L9,9M18.5,12C18.5,10.23 17.5,8.71 16,7.97V16C17.5,15.29 18.5,13.76 18.5,12Z"
                />
              </svg>
              <svg
                className="videoplayer-volume-muted-icon"
                viewBox="0 0 24 24"
              >
                <path
                  fill="currentColor"
                  d="M12,4L9.91,6.09L12,8.18M4.27,3L3,4.27L7.73,9H3V15H7L12,20V13.27L16.25,17.53C15.58,18.04 14.83,18.46 14,18.7V20.77C15.38,20.45 16.63,19.82 17.68,18.96L19.73,21L21,19.73L12,10.73M19,12C19,12.94 18.8,13.82 18.46,14.64L19.97,16.15C20.62,14.91 21,13.5 21,12C21,7.72 18,4.14 14,3.23V5.29C16.89,6.15 19,8.83 19,12M16.5,12C16.5,10.23 15.5,8.71 14,7.97V10.18L16.45,12.63C16.5,12.43 16.5,12.21 16.5,12Z"
                />
              </svg>
            </button>
            <input
              className="videoplayer-volume-slider"
              type="range"
              min="0"
              max="1"
              step="any"
              value={videoRef.current ? videoRef.current.volume : 1}
              onChange={handleVolumeChange}
              aria-label="Volume Slider"
            />
          </div>

          {/*------ Duration Display ------*/}
          <div className="videoplayer-duration-container">
            <div className="videoplayer-current-time">
              {formatDuration(currentTime)}
            </div>
            /
            <div className="videoplayer-total-time">
              {formatDuration(totalTime)}
            </div>
          </div>

          {/*------ Captions Button ------*/}
          {/* <button
            className="videoplayer-captions-btn"
            onClick={toggleCaptions}
            aria-label="Toggle Captions"
          >
            <svg viewBox="0 0 24 24">
              <path
                fill="currentColor"
                d="M18,11H16.5V10.5H14.5V13.5H16.5V13H18V14A1,1 0 0,1 17,15H14A1,1 0 0,1 13,14V10A1,1 0 0,1 14,9H17A1,1 0 0,1 18,10M11,11H9.5V10.5H7.5V13.5H9.5V13H11V14A1,1 0 0,1 10,15H7A1,1 0 0,1 6,14V10A1,1 0 0,1 7,9H10A1,1 0 0,1 11,10M19,4H5C3.89,4 3,4.89 3,6V18A2,2 0 0,0 5,20H19A2,2 0 0,0 21,18V6C21,4.89 20.1,4 19,4Z"
              />
            </svg>
          </button> */}

          {/*------ Playback Speed Button ------*/}
          <button
            className="videoplayer-speed-btn wide-btn"
            onClick={changePlaybackSpeed}
            title="Playback Speed"
            aria-label="Change Playback Speed"
          >
            {playbackRate}x
          </button>

          {/*------ Mini Player Button ------*/}
          {/* <button
            className="videoplayer-mini-player-btn"
            onClick={toggleMiniPlayerMode}
            aria-label="Mini Player"
          >
            <svg viewBox="0 0 24 24">
              <path
                fill="currentColor"
                d="M21 3H3c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h18c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H3V5h18v14zm-10-7h9v6h-9z"
              />
            </svg>
          </button> */}

          {/*------ Theater Mode Button ------*/}
          {/* <button
            className="videoplayer-theater-btn"
            onClick={toggleTheaterMode}
            aria-label="Theater Mode"
          >
            <svg className="videoplayer-tall" viewBox="0 0 24 24">
              <path
                fill="currentColor"
                d="M19 6H5c-1.1 0-2 .9-2 2v8c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2zm0 10H5V8h14v8z"
              />
            </svg>
            <svg className="videoplayer-wide" viewBox="0 0 24 24">
              <path
                fill="currentColor"
                d="M19 7H5c-1.1 0-2 .9-2 2v6c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V9c0-1.1-.9-2-2-2zm0 8H5V9h14v6z"
              />
            </svg>
          </button> */}

          {/*------ Full-Screen Button ------*/}
          <button
            className="videoplayer-full-screen-btn"
            onClick={toggleFullScreenMode}
            title="Full Screen"
            aria-label="Full Screen"
            // style={{paddingRight:"10px"}}
          >
            <svg className="videoplayer-open" viewBox="0 0 24 24">
              <path
                fill="currentColor"
                d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z"
              />
            </svg>
            <svg className="videoplayer-close" viewBox="0 0 24 24">
              <path
                fill="currentColor"
                d="M5 16h3v3h2v-5H5v2zm3-8H5v2h5V5H8v3zm6 11h2v-3h3v-2h-5v5zm2-11V5h-2v5h5V8h-3z"
              />
            </svg>
          </button>
        </div>
      </div>
      <video
        className="videoplayer-video-style"
        // src="https://tto-asset.s3.ap-south-1.amazonaws.com/Anavu/Video/WhatsApp-Video-2024-09-21-at-08.08.32.mp4"
        src={videoUrl}
        ref={videoRef}
        tabIndex="0"
        autoPlay
      >
        <track
          kind="captions"
          srcLang="en"
          src="assets/subtitles.vtt"
          default
        />
      </video>
    </div>
  );
};

export default VideoPlayer;
